import React, { useState, useEffect } from 'react';
import { 
  getUserNotifications, 
  markNotificationRead, 
  markAllNotificationsRead as markAllNotificationsReadAPI, 
  deleteNotification as deleteNotificationAPI 
} from '../apiClient';

interface Notification {
  id: number;
  type: 'streak' | 'achievement' | 'reminder' | 'challenge' | 'social' | 'update';
  title: string;
  message: string;
  time: string;
  read: boolean;
  icon: string;
  color: string;
  action?: {
    label: string;
    link: string;
  };
}

const NotificationsPanel: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    total: number;
    unread: number;
    by_type: Record<string, number>;
  }>({ total: 0, unread: 0, by_type: {} });

  // Load notifications on mount
  useEffect(() => {
    loadNotifications();
  }, []);

  /**
   * Load notifications from API
   */
  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getUserNotifications({ page: 1, limit: 50 } as any);
      
      if (response.success) {
        setNotifications(response.data.notifications);
        setStats(response.data.stats);
      } else {
        setError(response.message || 'Failed to load notifications');
      }
    } catch (err: any) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = stats.unread || notifications.filter(n => !n.read).length;

  /**
   * Mark notification as read
   */
  const markAsRead = async (id: number) => {
    try {
      // Optimistic update
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      setStats({ ...stats, unread: Math.max(0, stats.unread - 1) });
      
      await markNotificationRead(id);
    } catch (error) {
      console.error('Failed to mark as read:', error);
      // Revert on error
      await loadNotifications();
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      // Optimistic update
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setStats({ ...stats, unread: 0 });
      
      await markAllNotificationsReadAPI();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      // Revert on error
      await loadNotifications();
    }
  };

  /**
   * Delete notification
   */
  const deleteNotification = async (id: number) => {
    try {
      const notification = notifications.find(n => n.id === id);
      
      // Optimistic update
      setNotifications(notifications.filter(n => n.id !== id));
      setStats({
        ...stats,
        total: stats.total - 1,
        unread: notification && !notification.read ? stats.unread - 1 : stats.unread
      });
      
      await deleteNotificationAPI(id);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      // Revert on error
      await loadNotifications();
    }
  };

  const getTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'streak': 'Streak',
      'achievement': 'Achievement',
      'reminder': 'Reminder',
      'challenge': 'Challenge',
      'social': 'Social',
      'update': 'Update'
    };
    return labels[type] || 'Notification';
  };

  // Show loading state
  if (loading && notifications.length === 0) {
    return (
      <div className="notifications-panel-section mb-4">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && notifications.length === 0) {
    return (
      <div className="notifications-panel-section mb-4">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
          <div className="card-body text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <p className="text-danger">{error}</p>
            <button 
              className="btn btn-primary"
              onClick={loadNotifications}
            >
              <i className="fas fa-redo me-2"></i>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-panel-section mb-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div className="card-header bg-transparent border-0 p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}
              >
                <i className="fas fa-bell fa-lg"></i>
              </div>
              <div>
                <h4 className="fw-bold mb-0">Notifications</h4>
                <small className="text-muted">Stay updated with your learning journey</small>
              </div>
            </div>
            {unreadCount > 0 && (
              <button 
                className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                onClick={markAllAsRead}
                style={{ width: '40px', height: '40px' }}
                title="Mark All Read"
              >
                <i className="fas fa-check-double"></i>
              </button>
            )}
          </div>

          {/* Compact Stats Bar */}
          <div className="d-flex gap-2">
            <div 
              className="flex-fill text-center p-2 rounded-3 d-flex align-items-center justify-content-center gap-2"
              style={{ background: '#667eea15', border: '1px solid #667eea30' }}
              title="All Notifications"
            >
              <i className="fas fa-list text-primary"></i>
              <span className="fw-bold text-primary">{stats.total}</span>
            </div>
            <div 
              className="flex-fill text-center p-2 rounded-3 d-flex align-items-center justify-content-center gap-2"
              style={{ background: '#e74c3c15', border: '1px solid #e74c3c30' }}
              title="Unread Notifications"
            >
              <i className="fas fa-envelope" style={{ color: '#e74c3c' }}></i>
              <span className="fw-bold" style={{ color: '#e74c3c' }}>{unreadCount}</span>
            </div>
            <div 
              className="flex-fill text-center p-2 rounded-3 d-flex align-items-center justify-content-center gap-2"
              style={{ background: '#667eea15', border: '1px solid #667eea30' }}
              title="Reminders"
            >
              <i className="fas fa-bell text-primary"></i>
              <span className="fw-bold text-primary">{stats.by_type['reminder'] || 0}</span>
            </div>
            <div 
              className="flex-fill text-center p-2 rounded-3 d-flex align-items-center justify-content-center gap-2"
              style={{ background: '#f39c1215', border: '1px solid #f39c1230' }}
              title="Achievements"
            >
              <i className="fas fa-trophy" style={{ color: '#f39c12' }}></i>
              <span className="fw-bold" style={{ color: '#f39c12' }}>{stats.by_type['achievement'] || 0}</span>
            </div>
          </div>
        </div>

        <div className="card-body p-0">

          {/* Notifications List */}
          <div className="notifications-list" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className="notification-item p-4 border-bottom position-relative"
                  style={{
                    background: notification.read ? 'transparent' : '#f8f9fa',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => markAsRead(notification.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = notification.read ? '#f8f9fa' : '#e9ecef';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.read ? 'transparent' : '#f8f9fa';
                  }}
                >
                  {/* Unread Indicator */}
                  {!notification.read && (
                    <div 
                      className="position-absolute top-0 start-0 h-100"
                      style={{
                        width: '4px',
                        background: notification.color
                      }}
                    ></div>
                  )}

                  <div className="d-flex align-items-start">
                    {/* Icon */}
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{
                        width: '48px',
                        height: '48px',
                        background: notification.color + '20',
                        color: notification.color
                      }}
                    >
                      <i className={`${notification.icon} fa-lg`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <div>
                          <h6 className="fw-bold mb-1">{notification.title}</h6>
                          <span 
                            className="badge rounded-pill me-2"
                            style={{
                              background: notification.color + '15',
                              color: notification.color,
                              fontSize: '10px',
                              fontWeight: 600
                            }}
                          >
                            {getTypeLabel(notification.type)}
                          </span>
                        </div>
                        <div className="dropdown">
                          <button 
                            className="btn btn-sm btn-light rounded-circle"
                            type="button"
                            data-bs-toggle="dropdown"
                            style={{ width: '28px', height: '28px', fontSize: '12px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fas fa-ellipsis-v"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            {!notification.read && (
                              <li>
                                <button 
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}
                                >
                                  <i className="fas fa-check me-2"></i>Mark as Read
                                </button>
                              </li>
                            )}
                            <li>
                              <button 
                                className="dropdown-item text-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(notification.id);
                                }}
                              >
                                <i className="fas fa-trash me-2"></i>Delete
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <p className="mb-2 text-muted small">{notification.message}</p>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <i className="fas fa-clock me-1"></i>
                          {notification.time}
                        </small>
                        {notification.action && (
                          <button 
                            className="btn btn-sm rounded-pill px-3"
                            style={{
                              background: notification.color + '15',
                              color: notification.color,
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (notification.action) {
                                window.location.href = notification.action.link;
                              }
                            }}
                          >
                            {notification.action.label}
                            <i className="fas fa-arrow-right ms-1"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5">
                <i className="fas fa-bell-slash fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">No Notifications</h5>
                <p className="text-muted">You're all caught up! Check back later.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 text-center border-top">
              <button 
                className="btn btn-sm btn-link text-primary"
                onClick={() => {}}
              >
                View All Notifications
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notification Settings Quick Access */}
      {/* <div className="mt-3 p-3 rounded-4" style={{ background: '#f8f9fa' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <small className="fw-semibold">Notification Preferences</small>
            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
              Manage what notifications you receive
            </p>
          </div>
          <button 
            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            onClick={() => {}}
          >
            <i className="fas fa-cog me-2"></i>
            Settings
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default NotificationsPanel;
